import { TFunction } from 'i18next'
import {
  ImportNationalContractReferenceInput,
  NationalContractLevelEnum,
  NationalContractReferenceWithTypeDto,
  NationalContractType,
  UpdateNationalContractReferenceInput,
} from '../../../../types'
import { NATIONAL_CONTRACT_REFERENCE_IMPORT_ALL, NationalContractReferenceImportScope } from './nationalContractReferenceExcelTemplate'

export type NationalContractReferenceExcelSheet = {
  sheetName: string
  rows: Record<string, unknown>[]
}

export type NationalContractReferenceImportParseResult =
  | { success: true; input: ImportNationalContractReferenceInput }
  | { success: false; error: string }

const normalizeHeader = (value: string): string => value.trim().toLowerCase()

const normalizeSheetName = (value: string): string => value.trim().toUpperCase()

const sheetNameMatchesType = (sheetName: string, type: NationalContractType): boolean => {
  const normalizedSheetName = normalizeSheetName(sheetName)
  const normalizedType = normalizeSheetName(type)
  return normalizedSheetName === normalizedType || normalizedSheetName === normalizeSheetName(type.substring(0, 31))
}

const findColumnKey = (rowKeys: string[], expectedHeaders: string[]): string | undefined => {
  const normalizedExpectedHeaders = expectedHeaders.map(normalizeHeader)
  return rowKeys.find((key) => normalizedExpectedHeaders.includes(normalizeHeader(key)))
}

const parseLevelValue = (value: unknown): NationalContractLevelEnum | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const normalizedValue = String(value).trim().toUpperCase()
  return Object.values(NationalContractLevelEnum).find((level) => level.toUpperCase() === normalizedValue)
}

const parseMinSalary = (value: unknown, currentMinSalary: number): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return currentMinSalary
  }

  const parsedValue = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return undefined
  }

  return parsedValue
}

const parseSheetRows = (
  reference: NationalContractReferenceWithTypeDto,
  rows: Record<string, unknown>[],
  t: TFunction,
): UpdateNationalContractReferenceInput | string => {
  if (!rows.length) {
    return t('companySettingsView.nationalContractReferenceTab.importModal.errors.emptySheet', {
      contractType: t(`employee.nationalContract.type.${reference.type}`),
    })
  }

  const levelHeader = t('companySettingsView.nationalContractReferenceTab.editModal.columns.level')
  const newMinSalaryHeader = t('companySettingsView.nationalContractReferenceTab.editModal.columns.newMinSalary')
  const rowKeys = Object.keys(rows[0] ?? {})
  const levelColumnKey = findColumnKey(rowKeys, [levelHeader, 'level'])
  const newMinSalaryColumnKey = findColumnKey(rowKeys, [newMinSalaryHeader, 'new min. salary', 'new min salary'])

  if (!levelColumnKey) {
    return t('companySettingsView.nationalContractReferenceTab.importModal.errors.missingLevelColumn', {
      contractType: t(`employee.nationalContract.type.${reference.type}`),
    })
  }

  if (!newMinSalaryColumnKey) {
    return t('companySettingsView.nationalContractReferenceTab.importModal.errors.missingNewSalaryColumn', {
      contractType: t(`employee.nationalContract.type.${reference.type}`),
    })
  }

  const selectableLevels = reference.levels.filter((level) => level.selectable)
  const seenLevels = new Set<NationalContractLevelEnum>()
  const levelUpdates: UpdateNationalContractReferenceInput['levels'] = []

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    const levelValue = row[levelColumnKey]
    if (levelValue === null || levelValue === undefined || levelValue === '') {
      continue
    }

    const parsedLevel = parseLevelValue(levelValue)
    if (!parsedLevel) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.errors.unknownLevel', {
        level: String(levelValue),
        contractType: t(`employee.nationalContract.type.${reference.type}`),
        rowNo: rowIndex + 2,
      })
    }

    const selectableLevel = selectableLevels.find((level) => level.level === parsedLevel)
    if (!selectableLevel) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.errors.unknownLevel', {
        level: String(levelValue),
        contractType: t(`employee.nationalContract.type.${reference.type}`),
        rowNo: rowIndex + 2,
      })
    }

    if (seenLevels.has(parsedLevel)) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.errors.duplicateLevel', {
        level: parsedLevel,
        contractType: t(`employee.nationalContract.type.${reference.type}`),
      })
    }

    const parsedMinSalary = parseMinSalary(row[newMinSalaryColumnKey], selectableLevel.minSalary)
    if (parsedMinSalary === undefined) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.errors.invalidMinSalary', {
        contractType: t(`employee.nationalContract.type.${reference.type}`),
        rowNo: rowIndex + 2,
      })
    }

    seenLevels.add(parsedLevel)
    levelUpdates.push({
      level: parsedLevel,
      minSalary: parsedMinSalary,
    })
  }

  const missingLevel = selectableLevels.find((level) => !seenLevels.has(level.level as NationalContractLevelEnum))
  if (missingLevel) {
    return t('companySettingsView.nationalContractReferenceTab.importModal.errors.missingLevel', {
      level: missingLevel.level,
      contractType: t(`employee.nationalContract.type.${reference.type}`),
    })
  }

  return {
    type: reference.type,
    levels: levelUpdates,
  }
}

export const parseNationalContractReferenceExcelImport = (
  sheets: NationalContractReferenceExcelSheet[],
  scope: NationalContractReferenceImportScope,
  references: NationalContractReferenceWithTypeDto[],
  t: TFunction,
): NationalContractReferenceImportParseResult => {
  if (!sheets.length) {
    return { success: false, error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.emptyFile') }
  }

  if (scope === NATIONAL_CONTRACT_REFERENCE_IMPORT_ALL) {
    if (sheets.length !== references.length) {
      return {
        success: false,
        error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.scopeMismatchAll', {
          expectedCount: references.length,
          actualCount: sheets.length,
        }),
      }
    }

    const matchedReferences = new Set<NationalContractType>()
    const updates: UpdateNationalContractReferenceInput[] = []

    for (const sheet of sheets) {
      const reference = references.find((item) => sheetNameMatchesType(sheet.sheetName, item.type))
      if (!reference || matchedReferences.has(reference.type)) {
        return {
          success: false,
          error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.extraSheet', {
            sheetName: sheet.sheetName,
          }),
        }
      }

      matchedReferences.add(reference.type)
      const parsedSheet = parseSheetRows(reference, sheet.rows, t)
      if (typeof parsedSheet === 'string') {
        return { success: false, error: parsedSheet }
      }

      updates.push(parsedSheet)
    }

    const missingType = references.find((reference) => !matchedReferences.has(reference.type))
    if (missingType) {
      return {
        success: false,
        error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.missingSheet', {
          contractType: t(`employee.nationalContract.type.${missingType.type}`),
        }),
      }
    }

    const hasAnyChange = updates.some((update) =>
      update.levels.some((levelUpdate) => {
        const reference = references.find((item) => item.type === update.type)
        const currentLevel = reference?.levels.find((level) => level.level === levelUpdate.level)
        return currentLevel && currentLevel.minSalary !== levelUpdate.minSalary
      }),
    )

    if (!hasAnyChange) {
      return { success: false, error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.noChanges') }
    }

    return { success: true, input: { updates } }
  }

  if (sheets.length !== 1) {
    return {
      success: false,
      error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.scopeMismatchSingle'),
    }
  }

  const reference = references.find((item) => item.type === scope)
  if (!reference) {
    return { success: false, error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.unknown') }
  }

  if (!sheetNameMatchesType(sheets[0].sheetName, scope)) {
    return {
      success: false,
      error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.scopeMismatchSingle'),
    }
  }

  const parsedSheet = parseSheetRows(reference, sheets[0].rows, t)
  if (typeof parsedSheet === 'string') {
    return { success: false, error: parsedSheet }
  }

  const hasAnyChange = parsedSheet.levels.some((levelUpdate) => {
    const currentLevel = reference.levels.find((level) => level.level === levelUpdate.level)
    return currentLevel && currentLevel.minSalary !== levelUpdate.minSalary
  })

  if (!hasAnyChange) {
    return { success: false, error: t('companySettingsView.nationalContractReferenceTab.importModal.errors.noChanges') }
  }

  return { success: true, input: { updates: [parsedSheet] } }
}
